import { COLORS } from '../theme'
import { OUTCOMES, FOUNDATION_PILLARS } from '../scene/contract'

/**
 * Honest, crafted static experience for no-WebGL devices and users who prefer
 * reduced motion. No animation, no canvas — a Flower-of-Life hero in gold line-work
 * over the graphite void, the four outcomes, and the Foundation. Same story, still.
 */
function flowerCenters(r: number): Array<[number, number]> {
  // Triangular lattice points within 2r of origin → classic 19-circle Flower of Life.
  const pts: Array<[number, number]> = []
  const ax: [number, number] = [r, 0]
  const bx: [number, number] = [r / 2, (r * Math.sqrt(3)) / 2]
  for (let i = -2; i <= 2; i++) {
    for (let j = -2; j <= 2; j++) {
      const x = i * ax[0] + j * bx[0]
      const y = i * ax[1] + j * bx[1]
      if (Math.hypot(x, y) <= 2.05 * r) pts.push([x, y])
    }
  }
  return pts
}

export default function Fallback({ reason }: { reason: 'no-webgl' | 'reduced-motion' }) {
  const R = 46
  const centers = flowerCenters(R)
  return (
    <div
      className="fallback"
      tabIndex={0}
      role="region"
      aria-label="Sovereign — one source of truth for your whole business"
    >
      <header className="fb-bar">
        <span className="fb-wordmark">
          SOVEREIGN<span className="fb-dot">.</span>
        </span>
        <span className="fb-note">
          {reason === 'reduced-motion' ? 'Still view · motion off' : 'Static view'}
        </span>
      </header>

      <main className="fb-hero">
        <svg className="fb-flower" viewBox="-130 -130 260 260" aria-hidden="true">
          <defs>
            <radialGradient id="fbGlow" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor={COLORS.gold300} stopOpacity="0.5" />
              <stop offset="60%" stopColor={COLORS.gold500} stopOpacity="0.12" />
              <stop offset="100%" stopColor={COLORS.gold500} stopOpacity="0" />
            </radialGradient>
          </defs>
          <circle cx="0" cy="0" r="120" fill="url(#fbGlow)" />
          {centers.map(([cx, cy], i) => (
            <circle
              key={i}
              cx={cx}
              cy={cy}
              r={R}
              fill="none"
              stroke={COLORS.gold500}
              strokeWidth="0.6"
              opacity={0.55}
            />
          ))}
          {/* Metatron seed points */}
          {centers
            .filter(([x, y]) => Math.hypot(x, y) <= 1.05 * R || Math.hypot(x, y) <= 0.01)
            .map(([cx, cy], i) => (
              <circle key={`n${i}`} cx={cx} cy={cy} r="2.4" fill={COLORS.gold300} />
            ))}
        </svg>

        <p className="fb-eyebrow">The system K2 Sells runs on every day</p>
        <h1 className="fb-h1">One source of truth for your whole business.</h1>
        <p className="fb-sub">
          Reach the business you already have, close every deal clean, make your market come
          looking for you — and bring online the layer that turns each closing into proof.
          All on one source of truth you own.
        </p>

        <ul className="fb-outcomes">
          {OUTCOMES.map((o) => (
            <li key={o.id} className="fb-outcome">
              <span className="fb-o-label">{o.label}</span>
              {o.tag && <span className="fb-o-tag">{o.tag}</span>}
              <span className="fb-o-promise">{o.promise}</span>
              <span className="fb-o-detail">{o.detail}</span>
              {o.maturity && <span className="fb-o-maturity">{o.maturity}</span>}
            </li>
          ))}
        </ul>

        <div className="fb-foundation">
          <span className="fb-f-eyebrow">The Foundation · one source of truth</span>
          <span className="fb-f-pillars">
            {FOUNDATION_PILLARS.map((p) => p.label).join('  ·  ')}
          </span>
          <span className="fb-f-promise">Your data is always yours — never hostage.</span>
        </div>
      </main>
    </div>
  )
}
